import { LayersList } from '@deck.gl/core/typed';
import { Box, ButtonGroup, styled } from '@mui/material';
import {
  Feature,
  FeatureCollection,
  featureCollection,
  MultiPolygon,
  Point,
  Polygon,
} from '@turf/turf';
import { FlexBox } from 'components/Structure';
import { useLogEvent } from 'components/hooks';
import { ThemeButton, ThemeTypography } from 'designSystem';
import JsonEditor from 'designSystem/Inputs/JsonEditor/JsonEditor';
import CustomMap, { ICustomMapRef, IMapPopup } from 'designSystem/Map/CustomMap';
import { calculateArea } from 'designSystem/Map/utils/map.utils';
import Icon from 'designSystem/Primitives/Icon/Icon';
import { Map } from 'mapbox-gl';
import React, { FC, Ref, useEffect, useMemo, useState } from 'react';
import useMeasure from 'react-use/lib/useMeasure';
import { Booleanish, booleanish } from 'types/booleanish.types';
import { Coordinates } from 'types/types';
import { v4 as uuid } from 'uuid';
import round from 'lodash/round';

export interface IGeoDataProps {
  marker?: Coordinates;
  /**
   * The default features to be shown on the map when the component is mounted
   * This can be neighbouring plots or any other features
   */
  defaultMapLayers?: LayersList | undefined;
  defaultCenter?: Coordinates;
  infoPopup?: IMapPopup;
  /** Reference to the component itself to call function inside of it */
  customRef?: Ref<ICustomMapRef>;
  /**
   * The zoom level of the map when the component is mounted, it will try to center the map on the marker if provided
   * @default 2
   */
  defaultZoomLevel?: number;
  defaultValue?: FeatureCollection<Polygon | Point | MultiPolygon>;
  onChange: (value: FeatureCollection<Polygon | Point | MultiPolygon>) => void;
  onMarkerPopupClose?: () => void;
}

const PLACEHOLDER = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: {
        plotId: 'abc-156',
      },
      geometry: {
        coordinates: [19.48262, 14.43176],
        type: 'Point',
      },
    },
    {
      type: 'Feature',
      properties: {
        plotId: 'abc-156',
      },
      geometry: {
        coordinates: [
          [
            [25.82545, 14.0557864],
            [24.7326, 11.838458],
            [27.0359, 11.33425],
            [29.7282, 13.43176],
            [25.82545, 14.0557],
          ],
        ],
        type: 'Polygon',
      },
    },
  ],
};

const EMPTY_FEATURE_COLLECTION: FeatureCollection<MultiPolygon | Polygon | Point> = {
  type: 'FeatureCollection',
  features: [],
};

const CultivatedAreasSize = styled(Box)<{ 'map-spacing': booleanish }>(
  ({ theme, 'map-spacing': mapSpacing }) => ({
    position: 'absolute',
    background: theme.custom.themeColors.grayScale[20],
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    zIndex: 3,
    right: theme.spacing(mapSpacing === 'true' ? 6 : 4),
    top: mapSpacing === 'true' ? 62 : theme.spacing(10),
  })
);

const Container = styled('div')(() => ({
  position: 'relative',
  height: '100%',
  overflow: 'hidden',
}));

const GeoData: FC<IGeoDataProps> = ({
  defaultValue = EMPTY_FEATURE_COLLECTION,
  defaultCenter,
  defaultMapLayers,
  defaultZoomLevel = 2,
  marker,
  infoPopup,
  customRef,
  onChange,
  onMarkerPopupClose,
}) => {
  const { logEvent } = useLogEvent();
  const [ref, size] = useMeasure<HTMLDivElement>();

  const [view, setView] = useState<'map' | 'json'>('map');
  // const [inputFormat, setInputFormat] = useState<CoordinateVersion>('lnglat');
  const [mapRef, setMapRef] = useState<Map | null>(null);

  /** Features drawn on map that are always in LngLat format from MapBox */
  const [displayedFeatureCollection, setDisplayedFeatureCollection] =
    useState<FeatureCollection<Polygon | Point | MultiPolygon>>(defaultValue);

  const currentPolygonSizeHa = useMemo(
    () => calculateArea(displayedFeatureCollection),
    [displayedFeatureCollection]
  );

  // For the geoJson editor we can remove the ids from the features since they are only needed for the draw tool to work correctly
  const geoJsonFeatureCollection = useMemo(() => {
    return featureCollection(
      displayedFeatureCollection.features.map(feature => {
        const { id, ...rest } = { id: undefined, ...feature };
        return rest;
      })
    );
  }, [displayedFeatureCollection]);

  /** Update default if controlled input */
  useEffect(() => {
    setDisplayedFeatureCollection(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    onChange(displayedFeatureCollection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedFeatureCollection]);

  /**
   * Update the map position when the location field changes
   */
  useEffect(() => {
    // Only update the map if it is shown and there are no features yet
    if (view === 'map' && !displayedFeatureCollection.features?.length && marker && mapRef) {
      mapRef.flyTo({
        center: [marker.lng, marker.lat],
        zoom: 14,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marker, mapRef]);

  const handleDrawCreate = (newFeature: Feature<Polygon | Point>) => {
    logEvent('DRAW_CREATE_POLYGON');
    setDisplayedFeatureCollection(prev => featureCollection([...prev.features, newFeature]));
  };

  const handleDrawUpdate = (updatedFeature: Feature<Polygon | Point>) => {
    logEvent('DRAW_UPDATE_POLYGON');
    setDisplayedFeatureCollection(prev =>
      featureCollection([
        ...prev.features.filter(({ id }) => id !== updatedFeature.id), // Remove old feature
        updatedFeature,
      ])
    );
  };

  const handleDrawDelete = (deleteFeature: Feature<Polygon | Point>) => {
    logEvent('DRAW_DELETE_POLYGON');
    setDisplayedFeatureCollection(prev =>
      featureCollection(prev.features.filter(({ id }) => id !== deleteFeature.id))
    );
  };

  const handleJsonChange = (value: FeatureCollection<Polygon | Point | MultiPolygon> | null) => {
    logEvent('ENTERED_GEO_JSON_DATA');
    if (!value) {
      setDisplayedFeatureCollection(EMPTY_FEATURE_COLLECTION);
      return;
    }
    const features = value.features;
    // We have to make sure each feature has a unique id for the drawing feature to work correctly
    setDisplayedFeatureCollection(
      featureCollection(
        features.map(feature =>
          !feature.id || features.filter(({ id }) => id === feature.id).length > 1
            ? { ...feature, id: uuid() }
            : feature
        )
      )
    );
  };

  return (
    <Box width="100%" height="100%" overflow="hidden">
      <FlexBox mb={1} gap={2}>
        <ButtonGroup>
          <ThemeButton className={view === 'map' ? 'selected' : ''} onClick={() => setView('map')}>
            Drawing tool
          </ThemeButton>

          <ThemeButton
            className={view === 'json' ? 'selected' : ''}
            onClick={() => setView('json')}
          >
            Raw data
          </ThemeButton>
        </ButtonGroup>

        <Icon name="arrow-line-left" color="gray-40" />
        <ThemeTypography variant="BODY_MEDIUM" color="GRAY_40">
          Already have polygon coordinates? Add raw data here.
        </ThemeTypography>
      </FlexBox>

      <Container>
        <CultivatedAreasSize map-spacing={Booleanish(view === 'map')}>
          <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80">
            Total area
          </ThemeTypography>
          <ThemeTypography variant="BODY_SMALL">
            {round(currentPolygonSizeHa, 2).toLocaleString('en-GB').replace(/,/g, ' ')} ha
          </ThemeTypography>
        </CultivatedAreasSize>

        {view === 'json' ? (
          <Box width="100%" height="calc(100% - 68px)" position="relative">
            <FlexBox marginY={2} justifyContent="space-between">
              <ThemeTypography variant="BODY_MEDIUM">
                Paste and manage multi-polygon data in text field below.
              </ThemeTypography>
              {/* Removed until this is actually needed. This gets too complicated with geojson know and it should follow the standard  */}
              {/* <ButtonGroup>
                <ThemeButton
                  className={inputFormat === 'latlng' ? 'selected' : ''}
                  onClick={() => setInputFormat('latlng')}
                >
                  Lat, Lng
                </ThemeButton>
                <ThemeButton
                  className={inputFormat === 'lnglat' ? 'selected' : ''}
                  onClick={() => setInputFormat('lnglat')}
                >
                  Lng, Lat
                </ThemeButton>
              </ButtonGroup> */}
            </FlexBox>
            <Box ref={ref} height="calc(100% - 98px)">
              <JsonEditor<FeatureCollection<Polygon | Point | MultiPolygon>>
                placeholder={PLACEHOLDER}
                value={geoJsonFeatureCollection}
                validationFormat="geojson-point-polygon"
                errorMessage="Invalid GeoJSON format. Please use the RFC 7946 standard. Allowed feature types are point, polygon and multi polygon."
                hintMessage="This is an example to illustrate the GeoJson data format."
                fileProperties={{
                  name: 'farm-plots',
                  type: 'application/geo+json',
                  extension: 'geojson',
                }}
                onChange={handleJsonChange}
                minHeight={size.height}
              />
            </Box>
          </Box>
        ) : (
          <Box width="100%" height="calc(100% - 50px)" position="relative">
            <Box marginY={2}>
              <ThemeTypography variant="BODY_MEDIUM">
                Draw polygon shapes below to mark the cultivation area of this farm.
              </ThemeTypography>
            </Box>
            <Box height="calc(100% - 44px)">
              <CustomMap
                customRef={customRef}
                layers={defaultMapLayers}
                infoPopup={infoPopup}
                defaultDrawFeatures={displayedFeatureCollection}
                mapStyle="satellite"
                initialViewState={
                  defaultCenter
                    ? {
                        latitude: defaultCenter?.lat,
                        longitude: defaultCenter?.lng,
                        zoom: defaultZoomLevel,
                      }
                    : undefined
                }
                markers={
                  marker
                    ? [
                        {
                          id: 'farm-location',
                          coordinate: [marker?.lng, marker?.lat],
                          tooltip: 'Location center of the farm',
                        },
                      ]
                    : undefined
                }
                config={{
                  enableDrawing: true,
                  enableMapStyleToggle: true,
                  enableCenterButton: true,
                }}
                markerOptions={{
                  customIcon: 'site-location-point',
                  color: 'light-violet',
                }}
                onDrawCreate={handleDrawCreate}
                onDrawUpdate={handleDrawUpdate}
                onDrawDelete={handleDrawDelete}
                onMarkerPopupClose={onMarkerPopupClose}
                onMapLoad={setMapRef}
              />
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default GeoData;
